import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem('authToken');

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: API,
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  },

  resetPassword: async (email, code, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, { email, code, new_password: newPassword });
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    return response.data;
  },
};

// User API
export const userAPI = {
  getProfile: async () => {
    const response = await apiClient.get('/user/profile');
    return response.data;
  },
  
  updateLanguage: async (language, voice_gender) => {
    const response = await apiClient.put('/user/language', { language, voice_gender });
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.post('/user/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  deleteAccount: async () => {
    const response = await apiClient.delete('/user/delete-account');
    return response.data;
  },
};

// Content API
export const contentAPI = {
  getContent: async (language, category) => {
    const response = await axios.get(`${API}/content/${language}/${category}`);
    return response.data;
  },
  
  getLanguages: async () => {
    const response = await axios.get(`${API}/languages`);
    return response.data;
  },
};

// Progress API
export const progressAPI = {
  getProgress: async (language) => {
    const response = await apiClient.get(`/progress/${language}`);
    return response.data;
  },
  
  markLearned: async (language, category, item_id) => {
    const response = await apiClient.post('/progress/mark-learned', {
      language,
      category,
      item_id,
    });
    return response.data;
  },
};

// Translation API
export const translationAPI = {
  translate: async (text, target_lang) => {
    const response = await axios.post(`${API}/translate`, { text, target_lang });
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  login: async (email, password) => {
    const response = await axios.post(`${API}/admin/login`, { email, password });
    return response.data;
  },
  
  getStats: async () => {
    const response = await axios.get(`${API}/admin/stats`);
    return response.data;
  },

  getUsers: async (page = 1, limit = 50, filterType = null) => {
    const params = new URLSearchParams({ page, limit });
    if (filterType) params.append('filter_type', filterType);
    const response = await axios.get(`${API}/admin/users?${params}`);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const token = localStorage.getItem('adminToken');
    const response = await axios.post(`${API}/admin/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  exportUsers: (filterType = null) => {
    const params = filterType ? `?filter_type=${filterType}` : '';
    return `${API}/admin/users/export${params}`;
  },

  exportLanguages: () => `${API}/admin/languages/export`,
  
  exportLessons: () => `${API}/admin/lessons/export`,
  
  exportSignups: () => `${API}/admin/signups/export`,
};

// Public API (no auth required)
export const publicAPI = {
  getStats: async () => {
    const response = await axios.get(`${API}/public/stats`);
    return response.data;
  },
};

export default {
  authAPI,
  userAPI,
  contentAPI,
  progressAPI,
  translationAPI,
  adminAPI,
  publicAPI,
};
